/**
 * The customer's link code from a "Finish joining" / "Welcome" / free-course email, kept in
 * this tab's sessionStorage between the link page (/verify) and the next step.
 *
 * The code never goes into a URL query (simulation-v2.md §16 #4): /verify reads it from the
 * `#code=` fragment, keeps it here, and
 *   - a sale's checkout page reads it to send as `saleCode` (it approves the plan and carries
 *     the promo code);
 *   - a Google / Microsoft / Facebook sign-in keeps the server's one-time nonce here across
 *     the provider round-trip, and comes back to /verify?oauth=1 to finish.
 * sessionStorage can be unavailable (private browsing): every read and write is guarded.
 */

const SALE_KEY = 'alfie.saleLink';
const OAUTH_KEY = 'alfie.linkOAuth';
const MAX_AGE_MS = 6 * 60 * 60 * 1000;

export interface StoredSaleLink {
  code: string;
  /** The plan as the checkout page's ?plan= takes it, e.g. 'K2K'. */
  plan: string;
  savedAt: number;
}

export interface StoredLinkOAuth {
  nonce: string;
  code: string;
  savedAt: number;
}

function read<T extends { savedAt: number }>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const value = JSON.parse(raw) as T;
    if (typeof value?.savedAt !== 'number' || Date.now() - value.savedAt > MAX_AGE_MS) {
      sessionStorage.removeItem(key);
      return null;
    }
    return value;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Private browsing: the checkout then falls back to the normal approval check.
  }
}

function remove(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // nothing to do
  }
}

export function saveSaleLink(code: string, plan: string): void {
  write(SALE_KEY, { code, plan, savedAt: Date.now() } satisfies StoredSaleLink);
}

export function readSaleLink(): StoredSaleLink | null {
  return read<StoredSaleLink>(SALE_KEY);
}

export function clearSaleLink(): void {
  remove(SALE_KEY);
}

export function saveLinkOAuth(nonce: string, code: string): void {
  write(OAUTH_KEY, { nonce, code, savedAt: Date.now() } satisfies StoredLinkOAuth);
}

/** Read once: the nonce is single-use on the server too. */
export function consumeLinkOAuth(): StoredLinkOAuth | null {
  const value = read<StoredLinkOAuth>(OAUTH_KEY);
  remove(OAUTH_KEY);
  return value;
}

/** The `code` from a `#code=…` fragment (43 base64url characters), or null. */
export function codeFromHash(hash: string): string | null {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const code = params.get('code')?.trim() ?? '';
  return /^[A-Za-z0-9_-]{43}$/.test(code) ? code : null;
}
